import { AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';
import AWS from 'aws-sdk/global';
import qs from 'qs';

import config from '../config';
import parseJson from '../utils/json';

const poolData = {
  UserPoolId: config.cognito.USER_POOL_ID,
  ClientId: config.cognito.APP_CLIENT_ID,
};


function getCurrentUser() {
  const userPool = new CognitoUserPool(poolData);
  return userPool.getCurrentUser();
}

export function completeNewPasswordChallenge(user, newPassword) {
  return new Promise((resolve, reject) =>
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    }));
}

export function authenticate(user, email, password) {
  const authenticationData = {
    Username: email,
    Password: password,
  };
  const authenticationDetails = new AuthenticationDetails(authenticationData);

  return new Promise((resolve, reject) =>
    user.authenticateUser(authenticationDetails, {
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
      // User was signed up by an admin and must provide new
      // password and required attributes, if any, to complete
      // authentication.
      newPasswordRequired: (userAttributes) => {
        // the api doesn't accept this field back
        delete userAttributes.email_verified;
        delete userAttributes.email;
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ name: 'newPasswordRequired', user, userAttributes });
      },
    }));
}

export function login(email, password) {
  const userPool = new CognitoUserPool(poolData);
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return authenticate(user, email, password);
}

export function signup(email, password) {
  const userPool = new CognitoUserPool(poolData);

  const attributeList = [];

  const dataEmail = {
    Name: 'email',
    Value: email,
  };

  const attributeEmail = new CognitoUserAttribute(dataEmail);
  attributeList.push(attributeEmail);

  return new Promise((resolve, reject) =>
    userPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }

      resolve(result.user);
    }));
}

export function confirm(user, confirmationCode) {
  return new Promise((resolve, reject) =>
    user.confirmRegistration(confirmationCode, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    }));
}

export function forgotPassword(email) {
  const userPool = new CognitoUserPool(poolData);
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) =>
    user.forgotPassword({
      onSuccess: result => resolve({ result, user }),
      onFailure: err => reject(err),
    }));
}

export function confirmPassword(user, verificationCode, newPassword) {
  return new Promise((resolve, reject) =>
    user.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    }));
}

export function changePassword(oldPassword, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    cognitoUser.getSession((err2) => {
      if (err2) reject(err2);
      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  });
}

export function signOutUser() {
  const currentUser = getCurrentUser();

  if (currentUser !== null) {
    currentUser.signOut();
  }

  if (AWS.config.credentials) {
    AWS.config.credentials.clearCachedId();
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
  }
}

export function getUserAttributes() {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    cognitoUser.getSession((err, session) => {
      if (err) reject(err);
      resolve(session.getIdToken().decodePayload());
    });
  });
}

function getUserToken(currentUser) {
  return new Promise((resolve, reject) => {
    currentUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export async function authUser() {
  if (
    AWS.config.credentials &&
    Date.now() < AWS.config.credentials.expireTime - 60000
  ) {
    return true;
  }

  const currentUser = getCurrentUser();

  if (currentUser === null) {
    return false;
  }

  await getUserToken(currentUser);

  return true;
}

export async function getCurrentUserToken() {
  const currentUser = getCurrentUser();

  if (currentUser === null) {
    return false;
  }

  const userToken = await getUserToken(currentUser);
  return userToken;
}

export async function invokeBackendApi({
  path,
  method = 'GET',
  headers = {},
  queryParams = {},
  body,
}) {
  if (!await authUser()) {
    throw new Error('User is not logged in');
  }

  if (headers.Authorization === undefined) {
    headers.Authorization = await getCurrentUserToken();
  }

  if (body === undefined || body === null || method === 'GET') {
    body = undefined;
    delete headers['Content-Type'];
  } else {
    body = JSON.stringify(body);
    if (headers['Content-Type'] === undefined) {
      headers['Content-Type'] = 'application/json';
    }
  }


  headers = Object.entries(headers).reduce((a, [key, value]) => {
    // eslint-disable-next-line no-unused-expressions
    a.append(key, value);
    return a;
  }, new Headers());

  let url = `${config.apiGateway.URL}${path}`;
  const queryString = qs.stringify(queryParams);

  if (queryString !== '') {
    url += `?${queryString}`;
  }

  const myRequest = new Request(url, {
    method,
    body,
    headers,
  });
  const handleErrors = (response) => {
    if (!response.ok) {
      throw Error("handleErrors", response.statusText);
    }
    return response;
  };

  return fetch(myRequest)
    .then(handleErrors)
    .then(parseJson);
}



// WEBPACK FOOTER //
// ./src/libs/awsLib.js